import React, { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import "./index.scss";
import { ReactComponent as CloseIcon } from "../../static/icons/close.svg";
import {
  CounselingField,
  requestState,
  UsePeriod,
} from "../../store/RequestStore";
import { RequestDialog } from "../../components/consult";
import { useLocalStorage } from "../../shared/utill";

type OfficeOption = {
  id: string;
  title: string;
  description: string;
  price: number;
  counselingType: CounselingField;
};

const formatMonthlyPrice = (value: number) =>
  `${value.toLocaleString("ko-KR")}원 / 월`;

const Consult3 = () => {
  const [request, setRequest] = useRecoilState(requestState);
  const resetRequest = useResetRecoilState(requestState);
  const [selectedOption, setSelectedOption] = useState<OfficeOption | null>(
    null
  );
  const [dialogVisible, setDialogVisible] = useState(false);
  const [, setPersistedRequest] = useLocalStorage("request", request);

  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: "(max-width: 1200px)",
  });

  useEffect(() => resetRequest(), [resetRequest]);
  useEffect(() => {
    if ((window as any).fbq) {
      (window as any).fbq("track", "Optimization1");
    }
  }, []);
  useEffect(() => {
    if ((window as any).karrotPixel) {
      (window as any).karrotPixel.track("ViewContent");
    }
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const officeOptions = useMemo<OfficeOption[]>(
    () => [
      {
        id: "single-basic",
        title: "1인실 Basic",
        description: "독립 공간",
        price: 300000,
        counselingType: CounselingField.SINGLE_BASIC,
      },
      {
        id: "single-premium",
        title: "1인실 Premium",
        description: "독립 공간, 창가 자리 or 넓은 공간",
        price: 360000,
        counselingType: CounselingField.SINGLE_PREMIUM,
      },
      {
        id: "double-basic",
        title: "2인실 Basic",
        description: "독립 공간",
        price: 480000,
        counselingType: CounselingField.DOUBLE_BASIC,
      },
      {
        id: "double-premium",
        title: "2인실 Premium",
        description: "독립 공간, 창가 자리 or 넓은 공간",
        price: 600000,
        counselingType: CounselingField.DOUBLE_PREMIUM,
      },
      {
        id: "open-seat",
        title: "오픈석(지정)",
        description: "지정된 개방 공간",
        price: 180000,
        counselingType: CounselingField.OPEN_SEAT,
      },
    ],
    []
  );

  const handleOptionClick = (option: OfficeOption) => {
    setSelectedOption(option);
    const updatedRequest = {
      ...request,
      counselingType: option.counselingType,
      usePeriod: UsePeriod._3,
    };
    setRequest(updatedRequest);
    setPersistedRequest(updatedRequest);
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.text = `
      var across_adn_complete_contain = new fn_across_adn_contain();
      var across_adn_complete_param = [];
      across_adn_complete_param = {"ut":"Complete","ui":"107325","uo":"types1"};
      across_adn_complete_contain.init(across_adn_complete_param);
    `;
    document.body.appendChild(script);
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    setDialogVisible(true);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
  };

  const handleSubmitSuccess = () => {
    setDialogVisible(false);
    navigate("/");
  };

  useEffect(() => {
    if (dialogVisible) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [dialogVisible]);

  return (
    <div className="consult consult-1">
      <div className="header-page-title-wrapper">
        <div className="back-button" onClick={() => navigate("/private-office")}>
          <CloseIcon />
        </div>
        {isMobile && <div className="dummy"></div>}
      </div>
      <div className="consult-content w-full px-0 md:flex md:justify-center md:px-5">
        <div className="content flex w-full flex-col items-center gap-10 md:max-w-[506px]">
          <div className="flex w-full flex-col items-center text-center text-[#454F5D]">
            <div className="flex flex-col gap-[6px]">
              <p className="text-[30px] font-bold leading-[42px] text-[#454F5D] md:text-[32px] md:leading-[44.8px]">
                오피스 타입을 선택해주세요
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col items-end gap-4 md:gap-[16px]">
            <div className="flex w-full flex-col gap-4 md:gap-[16px]">
              {officeOptions.map((option) => {
                const priceLabel = formatMonthlyPrice(option.price);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    className="flex min-h-[78px] w-full items-center justify-between rounded-[20px] bg-white px-8 md:py-6 py-4 text-left shadow-[0_2px_10px_rgba(24,33,50,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_20px_rgba(53,49,56,0.1)]"
                    aria-pressed={selectedOption?.id === option.id}
                  >
                    <div className="flex w-full flex-col gap-[3px] text-left">
                      <div className="text-[18px] font-bold leading-[25.2px] text-[#454F5D]">
                        {option.title}
                      </div>
                      <div className="text-[12px] font-[400] leading-[16.8px] text-[#A2A7AE]">
                        {option.description}
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col items-end text-right">
                      <p className="text-[18px] font-bold leading-[25.2px] text-[#454F5D] whitespace-nowrap">
                        {priceLabel}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="w-full text-[11px] font-medium leading-[12px] text-[#949CA8] text-right md:text-[12px]">
              VAT 별도
            </div>
          </div>
        </div>
      </div>
      <RequestDialog
        visible={dialogVisible}
        onClose={handleDialogClose}
        onSubmitSuccess={handleSubmitSuccess}
        defaultArea="경기 동탄점"
        isAreaFixed
      />
    </div>
  );
};

export default Consult3;
